type OtpErrorLike = {
  status?: number;
  errorCode?: string | null;
  message?: string;
};

export type EmailOtpErrorLabels = {
  emailExists?: string;
  emailPending?: string;
  rateLimited?: string;
  invalidCode?: string;
  alreadyUsed?: string;
  expired?: string;
  sendError?: string;
  verifyError?: string;
  generic?: string;
};

const isAxiosStatusNoise = (message: string) =>
  /request failed with status code\s*\d+/i.test(message);

/**
 * Maps OTP API failures to user-facing copy. Prefer errorCode/status over raw
 * axios messages like "Request failed with status code 409".
 */
export function getEmailOtpErrorMessage(
  error: OtpErrorLike | null | undefined,
  labels: EmailOtpErrorLabels,
  kind: "send" | "verify" = "verify",
): string {
  const code = error?.errorCode || undefined;
  const status = error?.status;
  const raw = (error?.message || "").trim();
  const fallback =
    kind === "send"
      ? labels.sendError || labels.generic || "Kunde inte skicka verifieringskod"
      : labels.verifyError || labels.generic || "Ogiltig verifieringskod";

  if (code === "EMAIL_PENDING") {
    return labels.emailPending || fallback;
  }
  if (code === "EMAIL_EXISTS" || status === 409) {
    return labels.emailExists || fallback;
  }
  if (
    code === "OTP_RATE_LIMITED" ||
    code === "OTP_TOO_MANY_ATTEMPTS" ||
    status === 429
  ) {
    return (
      labels.rateLimited ||
      "Du har gjort för många försök. Vänta en stund och försök igen."
    );
  }
  if (code === "OTP_ALREADY_USED") {
    return (
      labels.alreadyUsed ||
      "Koden är redan använd. Begär en ny kod."
    );
  }
  if (code === "OTP_EXPIRED") {
    return labels.expired || "Koden har gått ut. Skicka en ny kod.";
  }
  if (code === "OTP_INVALID" || status === 400) {
    return labels.invalidCode || fallback;
  }
  if (status === 500 || status === 503) {
    return labels.generic || fallback;
  }
  if (!raw || isAxiosStatusNoise(raw)) {
    return fallback;
  }
  return raw;
}
